import React, { useEffect, useState } from 'react';
import { ScorecardLicenseSearchRequest, ScorecardLicenseSummaryData } from '../../../Component/Model/Dto/ScorecardLicense';
import { defaultObject } from '../../../Component/Model/helpers';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { ActionType, TableColumn } from "../../../Component/Model/Table/Table";
import { useHistory } from 'react-router-dom';

import * as EEScorecardActions from '../../../Component/Common/Constants/EEScorecard.Actions';
import { DefaultCell } from '../../../Component/Common/Components/DataTable/DefaultCell';
import { ActionsCell } from '../../../Component/Common/Components/DataTable/ActionsCell';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { BEEScorecardService } from '../../../Component/Api/BEEScorecard/BEEScorecard';
import { useSetRecoilState } from 'recoil';
import { ConsolidationAtom } from '../../../State/Consolidation.Atom';

interface ScorecardSummaryRows {
  ID: number;
  ScorecardTypeName: string;
  IsConsolidated: string;
  NumberOfLicences: number;
  NumberOfAvailableScorecards: number;
  Actions: ActionType[];
}

interface ScorecardSummaryTable {
  columns: Array<TableColumn>;
  rows: Array<ScorecardSummaryRows>;
}


export const ScorecardLicenseSummary = () => {
  const history = useHistory();
  const [scorecardSummaryData, setScorecardSummaryData] = useState<ScorecardLicenseSummaryData[]>(defaultObject);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
  const [filteredItemsCount, setFilteredItemsCount] = useState<number>(1);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const isConsolidateScorecard = useSetRecoilState(ConsolidationAtom);
  // const [searchValue, setSearchValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);


  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  }

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  }

  // const searchValueChanged = (searchValue: string) => {
  //     setSearchValue(searchValue);
  // }

  const scorecardLicenseSearchRequest: ScorecardLicenseSearchRequest = {
    Draw: 2,
    PageNumber: pageNumber,
    PageSize: entriesPerPage.defaultValue!,
    SortColumnNames: ['ScorecardTypeName'],
    SortDirections: ['Ascending'],
    SearchValue: "",
    ConsolidationValues: null,
    IsGroup: false,
    ScorecardTypes: ["43"]
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const scorecardSummaryResult = await BEEScorecardService.getScorecardLicenseSummaryData(scorecardLicenseSearchRequest);
      setScorecardSummaryData(scorecardSummaryResult.data.dataObject);
      setFilteredItemsCount(scorecardSummaryResult.data.filteredItemsCount);
      setTotalItemsCount(scorecardSummaryResult.data.totalItemsCount);

    } catch (error) {
      console.log(error);
      setScorecardSummaryData(defaultObject);
      setFilteredItemsCount(1);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }

  const handleCreateScorecard = (id: string, IsConsolidated: boolean) => {
    isConsolidateScorecard(IsConsolidated);
    if (IsConsolidated) {
      history.push({
        pathname: `/ee/scorecard/${id}/consolidation`,
        state: {
          scorecardLicenseId: id,
          action: EEScorecardActions.create
        }
      });
    } else {
      history.push({
        pathname: `/ee/scorecard/${id}/general`,
        state: {
          scorecardLicenseId: id,
          action: EEScorecardActions.create
        }
      });
    }

  }

  function ScorecardSummaryTableMapper(dataObject: ScorecardLicenseSummaryData[]): ScorecardSummaryTable {

    const columns = [
      { Header: 'Scorecard Type', accessor: 'ScorecardTypeName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
      { Header: 'Consolidated Scorecard', accessor: 'IsConsolidated', Cell: ({ value }: any) => <DefaultCell value={value} /> },
      { Header: 'Number Of Licenses', accessor: 'NumberOfLicences', Cell: ({ value }: any) => <DefaultCell value={value} /> },
      { Header: 'Number Of Available Scorecards', accessor: 'NumberOfAvailableScorecards', Cell: ({ value }: any) => <DefaultCell value={value} /> },
      { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];
    if (dataObject.length > 0) {

      const rows = dataObject.map((item: ScorecardLicenseSummaryData, index: number) => ({
        ID: item.ScorecardTypeId,
        ScorecardTypeName: item.ScorecardTypeName,
        IsConsolidated: item.IsConsolidated ? 'Yes' : 'No',
        NumberOfLicences: item.NumberOfLicences,
        NumberOfAvailableScorecards: item.NumberOfAvailableScorecards,
        Actions: item.NumberOfAvailableScorecards > 0 ? [
          {
            key: item.NextScorecardLicenseIdToUse,
            label: 'Create New Scorecard',
            action: () => { handleCreateScorecard(item.NextScorecardLicenseIdToUse, item.IsConsolidated) },
            icon: {
              name: 'add_box  ',
              color: 'info',
            }
          }
        ] : [
          {
            key: item.NextScorecardLicenseIdToUse,
            label: 'No licenses available',
            action: () => { },
            icon: {
              name: 'do_disturb ',
              color: 'error',
            }
          }
        ]
      }));
      return { columns: columns, rows: rows };
    }
    else {
      return { columns: columns, rows: [] };
    }
  }

  useEffect(() => {
    fetchData();

  }, [entriesPerPage, pageNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = ScorecardSummaryTableMapper(scorecardSummaryData); //Map the API data into the table

  return (
    <>
      <MDTypography>Summary of Scorecard Licenses</MDTypography>
      <DataTable
        key={totalItemsCount}
        table={tableData!}
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemsCount}
        totalItemsCount={totalItemsCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        isLoading={isLoading}
      ></DataTable>
    </>
  )
}
